import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CLIENT_LIST_PATH } from '../config/routes';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  BuildingOfficeIcon,
  GlobeAltIcon,
  EnvelopeIcon,
  MapPinIcon,
  UserIcon,
  DocumentTextIcon,
  UsersIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  PlusIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  ServerIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  NoSymbolIcon,
  CircleStackIcon,
  CloudIcon,
} from '@heroicons/react/24/outline';
import { usePageTitle } from '../contexts/PageTitleContext';
import { useUnsavedChanges } from '../contexts/UnsavedChangesContext';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import DateTimePicker from '../components/DateTimePicker';
import Modal from '../components/Modal';

interface CompanyFormData {
  name: string;
  domain: string;
  description: string;
  website: string;
  logo_url: string;
  app_version: string;
  contact_person: string;
  contact_person_email: string;
  contact_email: string;
  address: string;
  contract_id: string;
  is_active: boolean;
  total_licenses: number;
  available_licenses: number;
  managed_by?: string | null;
  settings: {
    allowed_email_domains: string[];
    reader_licenses_per_full?: number | null;
    max_auditor_licenses?: number | null;
    max_allowed_devices_per_user?: number;
    max_folders_per_device?: number;
  };
}

const CompanyForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useTranslation(['companies', 'clientForm']);
  const { setPageTitle } = usePageTitle();
  const { setHasUnsavedChanges, setOnSave, setIsSubmitting } = useUnsavedChanges();
  const isEditMode = !!id && id !== 'new';
  const [currentStep, setCurrentStep] = useState(1);
  const { user } = useAuth();

  // Route protection: Only sysadmins can create new companies
  useEffect(() => {
    if (!isEditMode && user && user.role !== 'sysadmin') {
      navigate(CLIENT_LIST_PATH);
    }
  }, [isEditMode, user, navigate]);
  const [activeTab, setActiveTab] = useState<'basic' | 'subscription' | 'infrastructure'>(() => {
    if (isEditMode && id) {
      const saved = localStorage.getItem(`companyForm_activeTab_${id}`);
      const tab = (saved === 'basic' || saved === 'subscription' || saved === 'infrastructure') ? saved : 'basic';
      // Company admins can only access 'basic' tab
      if (user && user.role !== 'sysadmin' && (tab === 'subscription' || tab === 'infrastructure')) {
        return 'basic';
      }
      return tab;
    }
    return 'basic';
  });
  const [newDomainInput, setNewDomainInput] = useState('');
  const [domainTakenError, setDomainTakenError] = useState<string | null>(null);
  const [licensedUntil, setLicensedUntil] = useState<string>('');
  // CLI add-on (gated, billed separately from the main term).
  const [cliEnabled, setCliEnabled] = useState(false);
  const [cliLicensedUntil, setCliLicensedUntil] = useState<string>('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeletingCompany, setIsDeletingCompany] = useState(false);
  const [deleteCompanyProgressStep, setDeleteCompanyProgressStep] = useState(0);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [isSuspendingCompany, setIsSuspendingCompany] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [isReactivatingCompany, setIsReactivatingCompany] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- used by setCreatingInfisicalOrg in handleCreateInfisicalOrg; value used when Infisical create-org UI is wired
  const [creatingInfisicalOrg, setCreatingInfisicalOrg] = useState(false);
  const [infisicalOrgName, setInfisicalOrgName] = useState('');
  const [dbUseConnectionString, setDbUseConnectionString] = useState(true);
  const [dbConnectionString, setDbConnectionString] = useState('');
  const [dbHost, setDbHost] = useState('');
  const [dbPort, setDbPort] = useState('5432');
  const [dbUser, setDbUser] = useState('');
  const [dbPassword, setDbPassword] = useState('');
  const [dbDatabase, setDbDatabase] = useState('');
  const [dbSslMode, setDbSslMode] = useState('prefer');
  const [dbTestLoading, setDbTestLoading] = useState(false);
  const [dbSaveLoading, setDbSaveLoading] = useState(false);
  const [dbTestError, setDbTestError] = useState<string | null>(null);
  const [dbTestSuccess, setDbTestSuccess] = useState(false);
  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [backupBeforeReset, setBackupBeforeReset] = useState(true);
  const [dbBackupLoading, setDbBackupLoading] = useState(false);
  const [dbResetLoading, setDbResetLoading] = useState(false);
  const [allowResourcesSaving, setAllowResourcesSaving] = useState(false);
  const [showManagedByModal, setShowManagedByModal] = useState(false);
  const [managedBySearchTerm, setManagedBySearchTerm] = useState('');
  const [availableCompaniesForManagedBy, setAvailableCompaniesForManagedBy] = useState<any[]>([]);
  const [showSendInviteModal, setShowSendInviteModal] = useState(false);
  const [sendInvitePreview, setSendInvitePreview] = useState<{ subject: string; html_content: string } | null>(null);
  const [sendInviteSending, setSendInviteSending] = useState(false);
  const defaultSubscriptionNote = 'The subscription end date may be recalculated based on your registration date and contract terms.';
  const [sendInviteSubscriptionNote, setSendInviteSubscriptionNote] = useState(defaultSubscriptionNote);
  const formDataRef = useRef<CompanyFormData>({} as CompanyFormData);

  // License alert settings state (sysadmin override)
  interface LicenseAlertSettings {
    app: boolean;
    email: boolean;
    thresholds?: {
      license_count?: {
        enabled: boolean;
        value: number;
        unit: 'count' | 'percentage';
      };
      expiry_time?: {
        enabled: boolean;
        value: number;
        unit: 'minutes' | 'hours' | 'days' | 'weeks' | 'months';
      };
    };
  }

  const [licenseAlertSettings, setLicenseAlertSettings] = useState<LicenseAlertSettings | null>(null);
  // Store preserved notification_settings separately (not part of formData)
  const [preservedNotificationSettings, setPreservedNotificationSettings] = useState<any>(null);

  const [formData, setFormData] = useState<CompanyFormData>({
    name: '',
    domain: '',
    description: '',
    website: '',
    logo_url: '',
    app_version: 'latest',
    contact_person: '',
    contact_person_email: '',
    contact_email: '',
    address: '',
    contract_id: '',
    is_active: true,
    total_licenses: 0,
    available_licenses: 0,
    managed_by: null,
    settings: {
      allowed_email_domains: [],
      reader_licenses_per_full: undefined,
      max_auditor_licenses: undefined,
      max_allowed_devices_per_user: 3,
      max_folders_per_device: 5,
    },
  });

  // Fetch company data if editing
  const { data: companyData, isLoading } = useQuery({
    queryKey: ['company', id],
    queryFn: () => apiService.get(`/companies/${id}`).then(res => res.data),
    enabled: isEditMode,
  });

  // Fetch subscription for company (edit mode) – used to populate and save license/device limits
  const { data: subscriptionData } = useQuery({
    queryKey: ['subscription', 'company', id],
    queryFn: () => apiService.getSubscriptionByCompany(id!),
    enabled: isEditMode && !!id,
  });

  // Fetch company usages for reader license usage display (subscription tab; sysadmin only)
  const { data: companyUsages } = useQuery({
    queryKey: ['company-usages', id],
    queryFn: () => apiService.getCompanyUsages(id!),
    enabled: isEditMode && !!id && user?.role === 'sysadmin',
  });

  // Fetch company resources to check if Infisical org exists
  const { data: companyResources } = useQuery({
    queryKey: ['company-resources', id],
    queryFn: () => apiService.get(`/company-resources/${id}`).then(res => res.data),
    enabled: !!id && isEditMode,
  });

  // Fetch invitations for this company (edit mode, sysadmin) to show Send vs Resend and status
  const { data: companyInvitations } = useQuery({
    queryKey: ['invitations', 'company', id],
    queryFn: () => apiService.getInvitationsByCompany(id!),
    enabled: isEditMode && !!id && user?.role === 'sysadmin',
  });

  const { data: platformDefaults } = useQuery({
    queryKey: ['platform-defaults'],
    queryFn: () => apiService.getPlatformDefaults(),
    enabled: user?.role === 'sysadmin',
  });
  const platformMaxAuditorDefault = platformDefaults?.default_max_auditor_licenses ?? 0;
  const maxAuditorPlatformBootstrapRef = useRef(false);
  useEffect(() => {
    if (isEditMode || user?.role !== 'sysadmin') return;
    if (!platformDefaults || maxAuditorPlatformBootstrapRef.current) return;
    maxAuditorPlatformBootstrapRef.current = true;
    const d = platformDefaults.default_max_auditor_licenses ?? 0;
    if (d <= 0) return;
    setFormData((prev) => ({
      ...prev,
      settings: {
        ...prev.settings,
        max_auditor_licenses: prev.settings.max_auditor_licenses ?? d,
      },
    }));
  }, [isEditMode, user?.role, platformDefaults]);
  // Seed CLI-enabled from the platform default on a NEW company.
  const cliPlatformBootstrapRef = useRef(false);
  useEffect(() => {
    if (isEditMode || user?.role !== 'sysadmin') return;
    if (!platformDefaults || cliPlatformBootstrapRef.current) return;
    cliPlatformBootstrapRef.current = true;
    setCliEnabled(!!platformDefaults.default_cli_enabled);
  }, [isEditMode, user?.role, platformDefaults]);
  const adminInvite = (() => {
    const adminEmail = (formData.contact_person_email || '').trim().toLowerCase();
    if (!adminEmail || !companyInvitations?.invitations?.length) return null;
    return companyInvitations.invitations.find(
      (inv: { invitee_email?: string; status?: string }) =>
        (inv.invitee_email || '').trim().toLowerCase() === adminEmail
    ) || null;
  })();
  const hasPendingAdminInvite = adminInvite?.status === 'pending';

  // Set page title
  useEffect(() => {
    if (isEditMode) {
      setPageTitle('Edit Client', companyData?.name || 'Loading...');
    } else {
      setPageTitle('Create Client', 'New Client');
    }
  }, [isEditMode, companyData, setPageTitle]);

  // Persist active tab to localStorage and ensure company admins can't access restricted tabs
  useEffect(() => {
    // Redirect company admins away from restricted tabs
    if (isEditMode && user && user.role !== 'sysadmin' && (activeTab === 'subscription' || activeTab === 'infrastructure')) {
      setActiveTab('basic');
      return;
    }
    
    if (isEditMode && id) {
      localStorage.setItem(`companyForm_activeTab_${id}`, activeTab);
    }
    
    // Cleanup: Clear tab from localStorage when component unmounts
    return () => {
      if (isEditMode && id) {
        localStorage.removeItem(`companyForm_activeTab_${id}`);
      }
    };
  }, [activeTab, isEditMode, id, user]);

  // Update ref whenever formData changes
  useEffect(() => {
    formDataRef.current = formData;
  }, [formData]);

  // Cleanup: Reset page title and unsaved changes when unmounting
  useEffect(() => {
    return () => {
      setPageTitle('Clients', 'Manage clients and their settings in the system');
      setHasUnsavedChanges(false);
      setOnSave(null);
    };
  }, [setPageTitle, setHasUnsavedChanges, setOnSave]);

  // Cycle progress message while company delete is in progress
  useEffect(() => {
    if (!isDeletingCompany) {
      setDeleteCompanyProgressStep(0);
      return;
    }
    const steps = 3;
    const interval = setInterval(() => {
      setDeleteCompanyProgressStep((prev) => (prev + 1) % steps);
    }, 2500);
    return () => clearInterval(interval);
  }, [isDeletingCompany]);

  // Fetch companies for managed_by when editing (to show current managed_by company name)
  useEffect(() => {
    if (isEditMode) {
      fetchCompaniesForManagedBy();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditMode, id]);

  // Load company data into form
  useEffect(() => {
    if (companyData) {
      // Parse settings if it's a string
      let parsedSettings = companyData.settings;
      if (typeof parsedSettings === 'string') {
        try {
          parsedSettings = JSON.parse(parsedSettings);
        } catch (e) {
          parsedSettings = { allowed_email_domains: [] };
        }
      }

      // License/device limits: prefer subscription when available (source of truth)
      const sub = subscriptionData?.subscription;
      const totalLicenses = sub?.max_full_licenses !== undefined ? sub.max_full_licenses : (companyData.total_licenses || 0);
      const availableLicenses = subscriptionData?.available_full_licenses !== undefined ? subscriptionData.available_full_licenses : (companyData.available_licenses ?? totalLicenses);
      const readerLicensesPerFull = sub?.reader_licenses_per_full !== undefined && sub?.reader_licenses_per_full !== null ? sub.reader_licenses_per_full : (parsedSettings?.reader_licenses_per_full ?? undefined);
      const maxAuditorLicenses = sub?.max_auditor_licenses !== undefined && sub?.max_auditor_licenses !== null ? sub.max_auditor_licenses : (parsedSettings?.max_auditor_licenses ?? undefined);
      const maxAllowedDevices = sub?.max_allowed_devices_per_user !== undefined ? sub.max_allowed_devices_per_user : (parsedSettings?.max_allowed_devices_per_user ?? 3);
      const maxFoldersPerDevice = sub?.max_folders_per_device !== undefined ? sub.max_folders_per_device : (parsedSettings?.max_folders_per_device ?? 5);
      const licensedUntilSource = sub?.licensed_until ?? parsedSettings?.licensed_until;
      if (licensedUntilSource) {
        const date = new Date(licensedUntilSource);
        if (!isNaN(date.getTime())) {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');
          setLicensedUntil(`${year}-${month}-${day} ${hours}:${minutes}:${seconds}`);
        } else {
          setLicensedUntil('');
        }
      } else {
        setLicensedUntil('');
      }

      // CLI add-on state from the subscription.
      setCliEnabled(!!(sub as any)?.cli_enabled);
      const cliUntilSource = (sub as any)?.cli_licensed_until as string | undefined;
      if (cliUntilSource) {
        const cd = new Date(cliUntilSource);
        if (!isNaN(cd.getTime())) {
          const y = cd.getFullYear();
          const mo = String(cd.getMonth() + 1).padStart(2, '0');
          const d = String(cd.getDate()).padStart(2, '0');
          const h = String(cd.getHours()).padStart(2, '0');
          const mi = String(cd.getMinutes()).padStart(2, '0');
          const s = String(cd.getSeconds()).padStart(2, '0');
          setCliLicensedUntil(`${y}-${mo}-${d} ${h}:${mi}:${s}`);
        } else {
          setCliLicensedUntil('');
        }
      } else {
        setCliLicensedUntil('');
      }

      // Extract license alert settings (sysadmin_override) from notification_settings
      if (parsedSettings?.notification_settings?.license_alert?.sysadmin_override) {
        setLicenseAlertSettings(parsedSettings.notification_settings.license_alert.sysadmin_override);
      } else {
        // Set to null if no override exists
        setLicenseAlertSettings(null);
      }

      // Preserve existing notification_settings (excluding license_alert.sysadmin_override which we handle separately)
      const preserved = parsedSettings?.notification_settings ? {
        ...parsedSettings.notification_settings,
        // We'll merge license_alert separately when saving
        license_alert: parsedSettings.notification_settings.license_alert ? {
          ...parsedSettings.notification_settings.license_alert,
          // Don't include sysadmin_override in preserved settings, we handle it separately
          sysadmin_override: undefined,
        } : undefined,
      } : undefined;

      setPreservedNotificationSettings(preserved);

      setFormData({
        name: companyData.name || '',
        domain: companyData.domain || '',
        description: companyData.description || '',
        website: companyData.website || '',
        logo_url: companyData.logo_url || '',
        app_version: companyData.app_version || 'latest',
        contact_person: companyData.contact_person || '',
        contact_person_email: companyData.contact_person_email || '',
        contact_email: companyData.contact_email || '',
        address: companyData.address || '',
        contract_id: companyData.contract_id || '',
        is_active: companyData.is_active ?? true,
        total_licenses: totalLicenses,
        available_licenses: availableLicenses,
        managed_by: companyData.managed_by || null,
        settings: {
          allowed_email_domains: parsedSettings?.allowed_email_domains || [],
          reader_licenses_per_full: readerLicensesPerFull,
          max_auditor_licenses: maxAuditorLicenses,
          max_allowed_devices_per_user: maxAllowedDevices,
          max_folders_per_device: maxFoldersPerDevice,
        },
      });
    }
  }, [companyData, subscriptionData]);

  // Fetch companies for managed_by selection modal
  const fetchCompaniesForManagedBy = async () => {
    try {
      const response = await apiService.get('/companies');
      // Filter out the current company (can't manage itself) - only in edit mode
      const companies = isEditMode && id 
        ? (response.data.companies || []).filter((c: any) => c.id !== id)
        : (response.data.companies || []);
      setAvailableCompaniesForManagedBy(companies);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
      toast.error(t('clientForm:failedToLoadClients'));
    }
  };

  // Filter companies by search term for managed_by
  const filteredManagedByCompanies = useMemo(() => {
    if (!managedBySearchTerm.trim()) return availableCompaniesForManagedBy;
    const term = managedBySearchTerm.toLowerCase();
    return availableCompaniesForManagedBy.filter((company: any) =>
      company.name.toLowerCase().includes(term) ||
      (company.domain && company.domain.toLowerCase().includes(term))
    );
  }, [availableCompaniesForManagedBy, managedBySearchTerm]);

  // Create/Update mutation
  const saveMutation = useMutation({
    mutationFn: async (data: CompanyFormData) => {
      // Convert licensedUntil from custom format "yyyy-mm-dd hh:mm:ss" to ISO 8601 format if set, or null if empty (unlimited)
      let licensedUntilISO: string | null = null;
      if (licensedUntil) {
        // Custom format: "2026-05-14 00:00:00" -> ISO 8601: "2026-05-14T00:00:00Z"
        const date = new Date(licensedUntil.replace(' ', 'T'));
        if (!isNaN(date.getTime())) {
          licensedUntilISO = date.toISOString(); // Converts to ISO 8601 format
        }
      }
      let cliLicensedUntilISO: string | null = null;
      if (cliLicensedUntil) {
        const cdate = new Date(cliLicensedUntil.replace(' ', 'T'));
        if (!isNaN(cdate.getTime())) cliLicensedUntilISO = cdate.toISOString();
      }
      // CLI add-on can't outlast the subscription term (must stay within bounds).
      if (cliLicensedUntilISO && licensedUntilISO && new Date(cliLicensedUntilISO) > new Date(licensedUntilISO)) {
        cliLicensedUntilISO = licensedUntilISO;
      }
      
      const cleanedSettings = {
        ...data.settings,
        // Do NOT include licensed_until, max_allowed_devices_per_user, max_folders_per_device in company settings – they live on subscription
        // Merge notification_settings: preserve existing and add/update license_alert.sysadmin_override
        notification_settings: (() => {
          const preserved = preservedNotificationSettings;
          const baseNotificationSettings = preserved || {};
          
          // Merge license_alert
          const existingLicenseAlert = baseNotificationSettings.license_alert || {};
          const updatedLicenseAlert = {
            ...existingLicenseAlert,
            ...(licenseAlertSettings ? { sysadmin_override: licenseAlertSettings } : {}),
          };
          
          // Only include notification_settings if there's something to save
          if (Object.keys(baseNotificationSettings).length > 0 || licenseAlertSettings) {
            return {
              ...baseNotificationSettings,
              license_alert: updatedLicenseAlert,
            };
          }
          return undefined;
        })(),
      };
      
      // Prepare company payload. For edit: exclude subscription fields (updated via PATCH /subscriptions/company/:companyId). For create: include total_licenses.
      let payload: any;
      if (isEditMode) {
        const { total_licenses: _t, available_licenses: _a, ...companyRest } = data;
        payload = {
          ...companyRest,
          settings: JSON.stringify(cleanedSettings),
          managed_by: data.managed_by || null,
        };
        // Only sysadmin can update domain and contract_id; don't send them for company admins
        if (user?.role !== 'sysadmin') {
          delete payload.domain;
          delete payload.contract_id;
        }
        await apiService.patch(`/companies/${id}`, payload);
        // Update subscription (license/device limits) – sysadmin only
        if (id && (user?.role === 'sysadmin')) {
          await apiService.updateSubscriptionByCompany(id, {
            max_full_licenses: data.total_licenses,
            reader_licenses_per_full: data.settings.reader_licenses_per_full ?? null,
            max_auditor_licenses: data.settings.max_auditor_licenses ?? null,
            max_allowed_devices_per_user: data.settings.max_allowed_devices_per_user ?? 3,
            max_folders_per_device: data.settings.max_folders_per_device ?? 5,
            licensed_until: licensedUntilISO != null ? licensedUntilISO : null,
            cli_enabled: cliEnabled,
            cli_licensed_until: cliLicensedUntilISO,
          });
        }
        return { data: { ...payload, settings: cleanedSettings } };
      } else {
        payload = {
          ...data,
          settings: JSON.stringify(cleanedSettings),
          managed_by: data.managed_by || null,
        };
        delete payload.available_licenses;
        return apiService.post('/companies', payload);
      }
    },
    onSuccess: async (response: any) => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      if (isEditMode) {
        queryClient.invalidateQueries({ queryKey: ['company', id] });
        queryClient.invalidateQueries({ queryKey: ['company-usages', id] });
        queryClient.invalidateQueries({ queryKey: ['subscription', 'company', id] });
      }
      
      // licensed_until is now on subscription; form already has it from state
      if (response?.data?.settings?.licensed_until) {
        try {
          const date = new Date(response.data.settings.licensed_until);
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            setLicensedUntil(`${year}-${month}-${day} ${hours}:${minutes}:${seconds}`);
          }
        } catch (_e) { /* ignore */ }
      }
      
      setHasUnsavedChanges(false);
      setIsSubmitting(false);
      toast.success(isEditMode ? t('clientForm:toasts.updated') : t('clientForm:toasts.created'));
      if (!isEditMode && response?.data?.admin_auto_assigned) {
        toast.success('Existing user automatically assigned as company admin.');
      }

      // After creating a new client: update subscription with form license/device limits, then navigate
      if (!isEditMode && response?.data?.id) {
        const data = formDataRef.current;
        let licensedUntilISO: string | null = null;
        if (licensedUntil) {
          const date = new Date(licensedUntil.replace(' ', 'T'));
          if (!isNaN(date.getTime())) licensedUntilISO = date.toISOString();
        }
        let cliLicensedUntilISO: string | null = null;
        if (cliLicensedUntil) {
          const cdate = new Date(cliLicensedUntil.replace(' ', 'T'));
          if (!isNaN(cdate.getTime())) cliLicensedUntilISO = cdate.toISOString();
        }
        if (cliLicensedUntilISO && licensedUntilISO && new Date(cliLicensedUntilISO) > new Date(licensedUntilISO)) {
          cliLicensedUntilISO = licensedUntilISO;
        }
        try {
          await apiService.updateSubscriptionByCompany(response.data.id, {
            max_full_licenses: data.total_licenses,
            reader_licenses_per_full: data.settings.reader_licenses_per_full ?? null,
            max_auditor_licenses: data.settings.max_auditor_licenses ?? null,
            max_allowed_devices_per_user: data.settings.max_allowed_devices_per_user ?? 3,
            max_folders_per_device: data.settings.max_folders_per_device ?? 5,
            licensed_until: licensedUntilISO,
            cli_enabled: cliEnabled,
            cli_licensed_until: cliLicensedUntilISO,
          });
        } catch (err: any) {
          toast.error(err.response?.data?.error || t('clientForm:toasts.createFailedSubscription'));
        }
        navigate(CLIENT_LIST_PATH);
      }
    },
    onError: (error: any) => {
      setIsSubmitting(false);
      // When backend rejects license reduction (cannot reduce below usage), it returns current subscription so we can restore the form
      const sub = error.response?.data?.subscription;
      if (sub) {
        setFormData((prev) => ({
          ...prev,
          total_licenses: sub.max_full_licenses ?? prev.total_licenses,
          settings: {
            ...prev.settings,
            reader_licenses_per_full: sub.reader_licenses_per_full ?? prev.settings.reader_licenses_per_full,
            max_auditor_licenses: sub.max_auditor_licenses ?? prev.settings.max_auditor_licenses,
            max_allowed_devices_per_user: sub.max_allowed_devices_per_user ?? prev.settings.max_allowed_devices_per_user,
            max_folders_per_device: sub.max_folders_per_device ?? prev.settings.max_folders_per_device,
          },
        }));
        if (sub.licensed_until) {
          const date = new Date(sub.licensed_until);
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');
            setLicensedUntil(`${year}-${month}-${day} ${hours}:${minutes}:${seconds}`);
          }
        }
        queryClient.invalidateQueries({ queryKey: ['subscription', 'company', id] });
      }
      toast.error(error.response?.data?.error || (isEditMode ? t('clientForm:toasts.updateFailed') : t('clientForm:toasts.createFailed')));
    },
  });

  const handleSave = useCallback(() => {
    // Use ref to get latest formData
    const currentFormData = formDataRef.current;
    
    // Validate required fields
    if (!currentFormData.name.trim()) {
      toast.error(t('clientForm:toasts.nameRequired'));
      return;
    }
    if (!currentFormData.domain.trim()) {
      toast.error(t('clientForm:toasts.domainRequired'));
      return;
    }
    if (!currentFormData.contact_email.trim()) {
      toast.error(t('clientForm:toasts.contactEmailRequired'));
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(currentFormData.contact_email)) {
      toast.error(t('clientForm:toasts.validContactEmail'));
      return;
    }
    // Admin email (contact_person_email) is required – invite link is sent to this address
    if (!currentFormData.contact_person_email.trim()) {
      toast.error(t('clientForm:toasts.adminEmailRequired'));
      return;
    }
    if (!emailRegex.test(currentFormData.contact_person_email)) {
      toast.error(t('clientForm:toasts.validAdminEmail'));
      return;
    }
    if (domainTakenError) {
      toast.error(domainTakenError);
      return;
    }
    // Validate license counts
    if (currentFormData.available_licenses > currentFormData.total_licenses) {
      toast.error(t('clientForm:toasts.availableExceedTotal'));
      return;
    }
    
    setIsSubmitting(true);
    saveMutation.mutate(currentFormData);
  }, [setIsSubmitting, saveMutation, domainTakenError, t]);

  const handleSetAllowCompanyAdminsFlags = useCallback(async (updates: { allow_company_admins_resources?: boolean; allow_company_admins_spin_up_db?: boolean; allow_company_admins_bring_own_db?: boolean; allow_company_admins_backup_db?: boolean }) => {
    if (!id) return;
    setAllowResourcesSaving(true);
    try {
      await apiService.patchCompanyResource(id, updates);
      queryClient.invalidateQueries({ queryKey: ['company-resources', id] });
      toast.success(t('clientForm:toasts.resourcesUpdated'));
    } catch (err: any) {
      toast.error(err.response?.data?.error || t('clientForm:toasts.updateSettingFailed'));
    } finally {
      setAllowResourcesSaving(false);
    }
  }, [id, queryClient, t]);

  // Set save function for header only in edit mode (create flow uses Next / Create company at the end)
  useEffect(() => {
    if (isEditMode) {
      setOnSave(() => handleSave);
      return () => setOnSave(null);
    }
    setOnSave(null);
    return () => setOnSave(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setOnSave, isEditMode]); // Removed handleSave from dependencies to prevent infinite loop

  const updateField = (field: keyof CompanyFormData, value: string | boolean | number | null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasUnsavedChanges(true);
  };

  const updateSettingsField = (path: string[], value: any) => {
    setFormData(prev => {
      const newSettings = { ...prev.settings };
      let current: any = newSettings;
      
      for (let i = 0; i < path.length - 1; i++) {
        current[path[i]] = { ...current[path[i]] };
        current = current[path[i]];
      }
      
      current[path[path.length - 1]] = value;
      return { ...prev, settings: newSettings };
    });
    setHasUnsavedChanges(true);
  };

  const addEmailDomain = async () => {
    if (!newDomainInput.trim()) return;
    
    const domain = newDomainInput.trim().toLowerCase();
    if (formData.settings.allowed_email_domains.includes(domain)) {
      toast.error(t('clientForm:toasts.domainAlreadyAdded'));
      return;
    }
    try {
      const { taken, company_name } = await apiService.checkDomainTaken(domain, id || undefined);
      if (taken) {
        toast.error(company_name ? t('clientForm:toasts.domainTaken', { name: company_name }) : t('clientForm:toasts.domainTakenAnother'));
        return;
      }
    } catch (err) {
      toast.error(t('clientForm:toasts.checkDomainFailed'));
      return;
    }
    updateSettingsField(['allowed_email_domains'], [...formData.settings.allowed_email_domains, domain]);
    setNewDomainInput('');
  };

  const removeEmailDomain = (domain: string) => {
    updateSettingsField(
      ['allowed_email_domains'],
      formData.settings.allowed_email_domains.filter(d => d !== domain)
    );
  };

  const handleNextStep = () => {
    if (currentStep === 1) {
      // Validate required fields for Step 1 (Basic Info)
      if (!formData.name.trim()) {
        toast.error(t('clientForm:toasts.nameRequired'));
        return;
      }
      if (!formData.domain.trim()) {
        toast.error(t('clientForm:toasts.domainRequired'));
        return;
      }
      if (!formData.contact_email.trim()) {
        toast.error(t('clientForm:toasts.contactEmailRequired'));
        return;
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.contact_email)) {
        toast.error(t('clientForm:toasts.validContactEmail'));
        return;
      }
      if (!formData.contact_person_email.trim()) {
        toast.error(t('clientForm:toasts.adminEmailRequired'));
        return;
      }
      if (!emailRegex.test(formData.contact_person_email)) {
        toast.error(t('clientForm:toasts.validAdminEmail'));
        return;
      }
      if (domainTakenError) {
        toast.error(domainTakenError);
        return;
      }
      
      // Auto-add domain to allowed_email_domains if not already present
      const domain = formData.domain.trim().toLowerCase();
      if (domain && !formData.settings.allowed_email_domains.includes(domain)) {
        updateSettingsField(['allowed_email_domains'], [...formData.settings.allowed_email_domains, domain]);
      }
      
      setCurrentStep(2);
    }
  };

  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = () => {
    if (formData.total_licenses < 0) {
      toast.error(t('clientForm:toasts.totalLicensesMin'));
      return;
    }
    if (formData.available_licenses < 0 || formData.available_licenses > formData.total_licenses) {
      toast.error(t('clientForm:toasts.availableLicensesRange'));
      return;
    }
    handleSave();
  };

  // Toggle Switch Component
  const ToggleSwitch: React.FC<{
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
  }> = ({ checked, onChange, disabled = false }) => (
    <button
      type="button"
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-governate-500 focus:ring-offset-2 ${
        checked ? 'bg-green-600' : 'bg-gray-200'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  // Step Indicator Component (for wizard)
  const StepIndicator: React.FC<{ step: number; title: string; isActive: boolean; isCompleted: boolean }> =
    ({ step, title, isActive, isCompleted }) => (
    <div className="flex items-center gap-3 flex-1 min-w-0">
      <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 shrink-0 transition-colors ${
        isCompleted ? 'bg-green-500 border-green-500 dark:bg-green-600 dark:border-green-600' :
        isActive ? 'bg-governate-500 border-governate-500 dark:bg-governate-600 dark:border-governate-600' :
        'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600'
      }`}>
        {isCompleted ? (
          <CheckCircleIcon className="w-5 h-5 text-white" />
        ) : (
          <span className={`text-sm font-semibold ${isActive ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`}>
            {step}
          </span>
        )}
      </div>
      <span className={`text-sm font-medium truncate ${isActive ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}`}>
        {title}
      </span>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-governate-500"></div>
      </div>
    );
  }

  // Shared input style for consistency
  const inputClass = 'w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-colors';
  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5';
  const helperClass = 'mt-1.5 text-xs text-gray-500 dark:text-gray-400';

  // Render Step 1: Basic Info (shared by both wizard and edit mode)
  const renderBasicInfo = () => (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column */}
        <div className="space-y-8">
          {/* Basic Information */}
          <div className="card border-l-4 border-l-governate-500/60 dark:border-l-governate-400/50">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-1">
                <div className="p-1.5 rounded-lg bg-governate-100 dark:bg-governate-900/30">
                  <BuildingOfficeIcon className="w-5 h-5 text-governate-600 dark:text-governate-400" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('clientForm:basicInfo.title')}</h2>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 ml-9">{t('form.sectionDescBasicInfo')}</p>
            </div>

            <div className="space-y-5">
              <div>
                <label className={labelClass}>{t('clientForm:basicInfo.clientName')}</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => updateField('name', e.target.value)}
                  className={inputClass}
                  placeholder={t('clientForm:basicInfo.placeholders.name')}
                />
              </div>

              <div>
                <label className={labelClass}>{t('clientForm:basicInfo.domain')}</label>
                <div className="flex items-center space-x-2">
                  <GlobeAltIcon className="w-5 h-5 text-gray-400" />
                  {user?.role === 'sysadmin' ? (
                    <input
                      type="text"
                      value={formData.domain}
                      onChange={(e) => {
                        updateField('domain', e.target.value);
                        setDomainTakenError(null);
                      }}
                      onBlur={async () => {
                        const d = formData.domain.trim().toLowerCase();
                        if (!d) {
                          setDomainTakenError(null);
                          return;
                        }
                        try {
                          const { taken, company_name } = await apiService.checkDomainTaken(d, id || undefined);
                          setDomainTakenError(taken ? (company_name ? t('clientForm:toasts.domainTaken', { name: company_name }) : t('clientForm:toasts.domainTakenAnother')) : null);
                        } catch {
                          setDomainTakenError(null);
                        }
                      }}
                      className={`flex-1 ${inputClass} ${domainTakenError ? 'border-red-500 dark:border-red-400' : ''}`}
                      placeholder={t('clientForm:basicInfo.placeholders.domain')}
                      required
                    />
                  ) : (
                    <input
                      type="text"
                      value={formData.domain}
                      readOnly
                      className={`flex-1 ${inputClass} bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed`}
                      placeholder={t('clientForm:basicInfo.placeholders.domain')}
                    />
                  )}
                </div>
                {domainTakenError && (
                  <p className="mt-1 text-sm text-red-600 dark:text-red-400">{domainTakenError}</p>
                )}
                {user?.role !== 'sysadmin' && (
                  <p className="mt-1 text-xs text-gray-500">{t('clientForm:onlySysadminChangeDomain')}</p>
                )}
              </div>

              <div>
                <label className={labelClass}>{t('clientForm:basicInfo.description')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => updateField('description', e.target.value)}
                  rows={3}
                  className={`${inputClass} resize-none`}
                  placeholder={t('clientForm:basicInfo.placeholders.description')}
                />
              </div>

              <div>
                <label className={labelClass}>Website</label>
                <div className="flex items-center space-x-2">
                  <GlobeAltIcon className="w-5 h-5 text-gray-400 dark:text-gray-500 shrink-0" />
                  <input
                    type="url"
                    value={formData.website}
                    onChange={(e) => updateField('website', e.target.value)}
                    className={`flex-1 ${inputClass}`}
                    placeholder={t('clientForm:basicInfo.placeholders.website')}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>{t('clientForm:basicInfo.logoUrl')}</label>
                <input
                  type="url"
                  value={formData.logo_url}
                  onChange={(e) => updateField('logo_url', e.target.value)}
                  className={inputClass}
                  placeholder={t('clientForm:basicInfo.placeholders.logoUrl')}
                />
              </div>
            </div>
          </div>

          {/* App Configuration */}
          <div className="card border-l-4 border-l-blue-500/50 dark:border-l-blue-400/40">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-1">
                <div className="p-1.5 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                  <DocumentTextIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('clientForm:appConfig.title')}</h2>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 ml-9">{t('form.sectionDescAppConfig')}</p>
            </div>

            <div className="space-y-5">
              <div>
                <label className={labelClass}>{t('clientForm:appConfig.appVersion')}</label>
                <select
                  value={formData.app_version}
                  onChange={(e) => updateField('app_version', e.target.value)}
                  className={inputClass}
                >
                  <option value="latest">{t('clientForm:appConfig.latest')}</option>
                  <option value="1.0.0">1.0.0</option>
                  <option value="0.9.0">0.9.0</option>
                </select>
              </div>

              {isEditMode && (
                <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                  <div>
                    <label className={labelClass}>{t('clientForm:appConfig.clientStatus')}</label>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {formData.is_active ? t('clientForm:appConfig.active') : t('clientForm:appConfig.inactive')}
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`text-sm ${formData.is_active ? 'text-gray-500' : 'text-gray-900'}`}>
                      {t('clientForm:appConfig.inactive')}
                    </span>
                    <ToggleSwitch
                      checked={formData.is_active}
                      onChange={(checked) => updateField('is_active', checked)}
                    />
                    <span className={`text-sm ${formData.is_active ? 'text-gray-900' : 'text-gray-500'}`}>
                      {t('clientForm:appConfig.active')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div className="space-y-8">
          {/* Contact Information */}
          <div className="card border-l-4 border-l-purple-500/50 dark:border-l-purple-400/40">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-1">
                <div className="p-1.5 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                  <UserIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('clientForm:contact.title')}</h2>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 ml-9">{t('form.sectionDescContact')}</p>
            </div>

            <div className="space-y-5">
              <div>
                <label className={labelClass}>{t('clientForm:contact.admin')}</label>
                <div className="flex items-center space-x-2">
                  <UserIcon className="w-5 h-5 text-gray-400 shrink-0" />
                  <input
                    type="text"
                    value={formData.contact_person}
                    onChange={(e) => updateField('contact_person', e.target.value)}
                    className={`flex-1 ${inputClass}`}
                    placeholder={t('clientForm:contact.placeholders.admin')}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>{t('clientForm:contact.adminEmail')}</label>
                <div className="flex items-center space-x-2">
                  <EnvelopeIcon className="w-5 h-5 text-gray-400 shrink-0" />
                  <input
                    type="email"
                    value={formData.contact_person_email}
                    onChange={(e) => updateField('contact_person_email', e.target.value)}
                    className={`flex-1 ${inputClass}`}
                    placeholder={t('clientForm:contact.placeholders.adminEmail')}
                    required
                  />
                </div>
                <p className={helperClass}>{t('clientForm:contact.inviteHint')}</p>
                {isEditMode && user?.role === 'sysadmin' && formData.contact_person_email.trim() && adminInvite?.status !== 'used' && (
                  <div className="mt-3">
                    {companyData?.admin_assigned ? (
                      <p className="text-xs text-green-600 dark:text-green-400">Admin account already active — no invite needed.</p>
                    ) : companyData?.admin_user_exists ? (
                      <>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">A user with this email already exists in the system.</p>
                        {formData.available_licenses <= 0 && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 mb-1">No full licenses available — allocate at least 1 full license before assigning.</p>
                        )}
                        <button
                          type="button"
                          disabled={formData.available_licenses <= 0}
                          onClick={async () => {
                            try {
                              await apiService.post(`/companies/${id}/assign-existing-admin`, {});
                              toast.success('User assigned as company admin.');
                              queryClient.invalidateQueries({ queryKey: ['company', id] });
                              queryClient.invalidateQueries({ queryKey: ['subscription', 'company', id] });
                            } catch (e: any) {
                              toast.error(e.response?.data?.error || 'Failed to assign user as admin.');
                            }
                          }}
                          className="px-4 py-2 text-sm font-medium rounded-lg border-2 border-gray-800 dark:border-gray-200 text-gray-900 dark:text-white bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Set as Admin
                        </button>
                      </>
                    ) : (
                      <>
                        {hasPendingAdminInvite && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Invitation pending</p>
                        )}
                        {formData.available_licenses <= 0 && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 mb-1">No full licenses available — allocate at least 1 full license before sending the admin invite.</p>
                        )}
                        <button
                          type="button"
                          disabled={formData.available_licenses <= 0}
                          onClick={async () => {
                            setShowSendInviteModal(true);
                            setSendInviteSubscriptionNote(defaultSubscriptionNote);
                            try {
                              const t = await apiService.getOrganisationInviteTemplate();
                              setSendInvitePreview({ subject: t.subject, html_content: t.html_content });
                            } catch (e: any) {
                              toast.error(e.response?.data?.error || t('clientForm:toasts.inviteTemplateFailed'));
                              setShowSendInviteModal(false);
                            }
                          }}
                          className="px-4 py-2 text-sm font-medium rounded-lg border-2 border-gray-800 dark:border-gray-200 text-gray-900 dark:text-white bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {hasPendingAdminInvite ? t('clientForm:buttons.resendInvite') : t('clientForm:buttons.sendInvite')}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className={labelClass}>{t('clientForm:contact.contactEmail')}</label>
                <div className="flex items-center space-x-2">
                  <EnvelopeIcon className="w-5 h-5 text-gray-400 shrink-0" />
                  <input
                    type="email"
                    value={formData.contact_email}
                    onChange={(e) => updateField('contact_email', e.target.value)}
                    className={`flex-1 ${inputClass}`}
                    placeholder={t('clientForm:contact.placeholders.contactEmail')}
                    required
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>{t('clientForm:contact.address')}</label>
                <div className="flex items-start space-x-2">
                  <MapPinIcon className="w-5 h-5 text-gray-400 mt-3 shrink-0" />
                  <textarea
                    value={formData.address}
                    onChange={(e) => updateField('address', e.target.value)}
                    rows={4}
                    className={`flex-1 ${inputClass} resize-none`}
                    placeholder={t('clientForm:contact.placeholders.address')}
                  />
                </div>
              </div>

              <div>
                <label className={labelClass}>{t('clientForm:contact.contractId')}</label>
                <div className="flex items-center space-x-2">
                  <DocumentTextIcon className="w-5 h-5 text-gray-400 shrink-0" />
                  {user?.role === 'sysadmin' ? (
                    <input
                      type="text"
                      value={formData.contract_id}
                      onChange={(e) => updateField('contract_id', e.target.value)}
                      className={`flex-1 ${inputClass}`}
placeholder={t('clientForm:contact.placeholders.contractId')}
                  />
                  ) : (
                    <input
                      type="text"
                      value={formData.contract_id}
                      readOnly
                      className={`flex-1 ${inputClass} bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed`}
                      placeholder={t('clientForm:contact.placeholders.contractId')}
                    />
                  )}
                </div>
                <p className={helperClass}>
                  {user?.role === 'sysadmin' ? t('clientForm:contact.contractIdHint') : t('clientForm:contact.onlySysadminChangeContractId')}
                </p>
              </div>

              {/* Managed By - Only for sysadmins (both create and edit) */}
              {user?.role === 'sysadmin' && (
                <div>
                  <label className={labelClass}>{t('clientForm:managedBy.label')}</label>
                  <div className="flex items-center gap-2">
                    {formData.managed_by ? (
                      <button
                        onClick={() => {
                          setShowManagedByModal(true);
                          fetchCompaniesForManagedBy();
                        }}
                        className="flex-1 text-left px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                      >
                        {(() => {
                          // First check if we have it in availableCompaniesForManagedBy
                          let managedByCompany = availableCompaniesForManagedBy.find((c: any) => c.id === formData.managed_by);
                          // If not found, check companyData (for initial load in edit mode)
                          if (!managedByCompany && isEditMode && companyData?.managed_by === formData.managed_by) {
                            // We need to fetch it - show loading or fetch on mount
                            return t('clientForm:loading');
                          }
                          return managedByCompany?.name || t('clientForm:unknownClient');
                        })()}
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setShowManagedByModal(true);
                          fetchCompaniesForManagedBy();
                        }}
                        className="flex-1 text-left px-3 py-2 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50 focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                      >
                        {t('clientForm:managedBy.noClientSelfManaged')}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setShowManagedByModal(true);
                        fetchCompaniesForManagedBy();
                      }}
                      className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                      title={t('clientForm:managedBy.selectManagingClient')}
                    >
                      <PencilIcon className="w-4 h-4" />
                    </button>
                  </div>
                  <p className={helperClass}>{t('clientForm:managedBy.hint')}</p>
                </div>
              )}
            </div>
          </div>
        </div>
    </div>
  );

  // Render Step 2: Subscription Settings (for wizard) - Only for sysadmins
  const renderSubscriptionSettings = () => {
    // Only sysadmins can see subscription settings
    if (user?.role !== 'sysadmin') {
      return (
        <div className="card">
          <div className="text-center py-8">
            <ShieldCheckIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">You don't have permission to view subscription settings.</p>
            <p className="text-sm text-gray-500 mt-2">Only system administrators can manage licenses and subscriptions.</p>
          </div>
        </div>
      );
    }

    const inputClass = 'max-w-[10rem] w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent dark:bg-gray-800 dark:text-white';

    const sub = subscriptionData?.subscription;
    const isSuspended = sub?.status === 'suspended';
    const suspendedAtFormatted = sub?.suspended_at != null
      ? (() => {
          const raw = String(sub.suspended_at);
          const d = new Date(raw);
          return isNaN(d.getTime()) ? raw : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`;
        })()
      : null;

    return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Suspended banner + Re-activate - show when subscription status is suspended */}
      {isSuspended && (
        <div className="lg:col-span-2 card border-l-4 border-l-amber-500/50 dark:border-l-amber-400/40 bg-amber-50/30 dark:bg-amber-900/10">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Client suspended</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Suspended at: <strong>{suspendedAtFormatted ?? '—'}</strong>. Users are in reader mode and inactive. Re-activate to restore access and apply any subscription changes (e.g. license count, valid until).
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowReactivateModal(true)}
              className="px-4 py-2.5 text-sm font-medium text-white bg-governate-600 hover:bg-governate-700 rounded-lg transition-colors flex items-center gap-2 shrink-0"
            >
              <CheckCircleIcon className="w-4 h-4" />
              Re-activate
            </button>
          </div>
        </div>
      )}

      {/* Left Column - License Management */}
      <div className="space-y-8">
        <div className="card border-l-4 border-l-governate-500/60 dark:border-l-governate-400/50">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 rounded-lg bg-governate-100 dark:bg-governate-900/30">
                <UsersIcon className="w-5 h-5 text-governate-600 dark:text-governate-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">License Management</h2>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 ml-9">{t('form.sectionDescLicenseManagement')}</p>
          </div>

          <div className="space-y-6">
            {/* 2x2 grid: Number of Licenses | Reader licenses per full license; then Max Devices | Max Folders */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Number of Licenses *
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.total_licenses}
                  onChange={(e) => {
                    const total = parseInt(e.target.value) || 0;
                    updateField('total_licenses', total);
                    if (!isEditMode) {
                      updateField('available_licenses', total);
                    } else {
                      const usedLicenses = (companyData?.total_licenses || 0) - (companyData?.available_licenses || 0);
                      const newAvailable = Math.max(0, total - usedLicenses);
                      updateField('available_licenses', newAvailable);
                    }
                  }}
                  className={inputClass}
                  placeholder="10"
                />
                <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">Maximum users that can be enrolled</p>
                {isEditMode && (
                  <div className="mt-3 px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-600 space-y-1">
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      <strong>Full licenses:</strong> {formData.total_licenses - formData.available_licenses} used,{' '}
                      {formData.available_licenses} available out of {formData.total_licenses} full seats
                    </p>
                    {companyUsages != null && user?.role === 'sysadmin' && (() => {
                      const effectiveAuditorMax =
                        companyUsages.auditor_licenses_max ??
                        (typeof formData.settings.max_auditor_licenses === 'number'
                          ? formData.settings.max_auditor_licenses
                          : null);
                      const totalMax =
                        (companyUsages.full_licenses_max ?? 0) +
                        (companyUsages.reader_licenses_max ?? 0) +
                        (effectiveAuditorMax ?? 0);
                      const totalUsed =
                        (companyUsages.full_licenses_used ?? 0) + (companyUsages.reader_licenses_used ?? 0);
                      const available = Math.max(0, totalMax - totalUsed);
                      return (
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          <strong>All seats (full + reader pool + auditors):</strong>{' '}
                          {totalUsed} used, {available} available out of {totalMax} total
                        </p>
                      );
                    })()}
                  </div>
                )}
              </div>

              {user?.role === 'sysadmin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Reader licenses per full license
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.settings.reader_licenses_per_full ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      const num = v === '' ? undefined : parseInt(v, 10);
                      updateSettingsField(['reader_licenses_per_full'], v === '' ? undefined : (Number.isNaN(num) ? formData.settings.reader_licenses_per_full : num));
                    }}
                    className={inputClass}
                    placeholder="Platform default"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Reader slots per full license. Empty = use platform default.
                  </p>
                  {isEditMode && companyUsages != null && (
                    <div className="mt-3 px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-600">
                      <p className="text-xs text-gray-600 dark:text-gray-400">
                        <strong>Reader License Usage (invite pool):</strong>{' '}
                        {companyUsages.reader_pool_licenses_used ??
                          Math.max(
                            0,
                            (companyUsages.reader_licenses_used ?? 0) - (companyUsages.auditor_licenses_used ?? 0)
                          )}{' '}
                        used,{' '}
                        {Math.max(
                          0,
                          (companyUsages.reader_licenses_max ?? 0) -
                            (companyUsages.reader_pool_licenses_used ??
                              Math.max(
                                0,
                                (companyUsages.reader_licenses_used ?? 0) - (companyUsages.auditor_licenses_used ?? 0)
                              ))
                        )}{' '}
                        available out of {companyUsages.reader_licenses_max ?? 0} total
                        {' '}
                        <span className="text-gray-500">
                          ({companyUsages.reader_licenses_used ?? 0} reader-type licenses incl. auditors)
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              )}

              {user?.role === 'sysadmin' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Max Auditor Licenses
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.settings.max_auditor_licenses ?? ''}
                    onChange={(e) => {
                      const v = e.target.value;
                      const num = v === '' ? undefined : parseInt(v, 10);
                      updateSettingsField(['max_auditor_licenses'], v === '' ? undefined : (Number.isNaN(num) ? formData.settings.max_auditor_licenses : num));
                    }}
                    className={inputClass}
                    placeholder="Unlimited"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Max company auditors allowed. Empty = unlimited.
                    {platformMaxAuditorDefault > 0 && (
                      <span>
                        {' '}
                        Platform default for new clients: {platformMaxAuditorDefault}
                        {!isEditMode ? ' (prefilled).' : '.'}
                      </span>
                    )}
                  </p>
                  {isEditMode && companyUsages != null && (() => {
                    const auditorMax =
                      companyUsages.auditor_licenses_max ??
                      (typeof formData.settings.max_auditor_licenses === 'number'
                        ? formData.settings.max_auditor_licenses
                        : null);
                    const auditorUsed = companyUsages.auditor_licenses_used ?? 0;
                    return (
                      <div className="mt-3 px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-600">
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          <strong>Auditor Usage:</strong> {auditorUsed} used
                          {auditorMax != null
                            ? `, ${Math.max(0, auditorMax - auditorUsed)} available out of ${auditorMax} total`
                            : ' (no cap)'}
                        </p>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {user?.role === 'sysadmin' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Max Allowed Devices per user *
                    </label>
                    <input
                      type="number"
                      min="3"
                      value={formData.settings.max_allowed_devices_per_user || 3}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 3;
                        const minValue = Math.max(3, value);
                        updateSettingsField(['max_allowed_devices_per_user'], minValue);
                      }}
                      className={inputClass}
                      placeholder="3"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Maximum non-web devices allowed per user (minimum: 3)
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Max Folders per Device *
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.settings.max_folders_per_device || 5}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 5;
                        const minValue = Math.max(1, value);
                        updateSettingsField(['max_folders_per_device'], minValue);
                      }}
                      className={inputClass}
                      placeholder="5"
                    />
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Maximum auto-encrypt folders per device for client users (default: 5)
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* License Valid Until - full width below */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">License Valid Until</label>
              <DateTimePicker
                value={licensedUntil || null}
                onChange={(value) => {
                  setLicensedUntil(value || '');
                  setHasUnsavedChanges(true);
                }}
                placeholder="Select license expiration date and time"
                minDate={new Date()}
                showTimeSelect={true}
                returnFormat="custom"
                dateFormat="yyyy-MM-dd HH:mm:ss"
                className="w-full max-w-md dark:bg-gray-900 dark:text-white dark:border-gray-600 rounded-lg"
              />
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                Set the license expiration date and time (Format: YYYY-MM-DD HH:MM:SS). Only future dates are allowed.
              </p>
            </div>

            {/* CLI add-on — gated, billed separately from the main term */}
            {user?.role === 'sysadmin' && (
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={cliEnabled}
                    onChange={(e) => { setCliEnabled(e.target.checked); setHasUnsavedChanges(true); }}
                    className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Enable CLI access (add-on)</span>
                </label>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Allows full-license users in this company to create CLI tokens and activate the CLI.
                  {platformDefaults ? ` Platform default: ${platformDefaults.default_cli_enabled ? 'on' : 'off'}.` : ''}
                </p>
                {cliEnabled && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">CLI add-on expires</label>
                    <DateTimePicker
                      value={cliLicensedUntil || null}
                      onChange={(value) => { setCliLicensedUntil(value || ''); setHasUnsavedChanges(true); }}
                      placeholder="Empty = no separate end date"
                      minDate={new Date()}
                      maxDate={licensedUntil ? new Date(licensedUntil.replace(' ', 'T')) : undefined}
                      showTimeSelect={true}
                      returnFormat="custom"
                      dateFormat="yyyy-MM-dd HH:mm:ss"
                      className="w-full max-w-md dark:bg-gray-900 dark:text-white dark:border-gray-600 rounded-lg"
                    />
                    <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                      Sold separately from the main term. Leave empty for no separate end date. Cannot exceed the License Valid Until date above.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* License Alert Settings - Sysadmin Override */}
        {user?.role === 'sysadmin' && (
          <div className="card border-l-4 border-l-orange-500/50 dark:border-l-orange-400/40 bg-orange-50/30 dark:bg-orange-900/10">
            <div className="mb-4">
              <div className="flex items-center gap-2 mb-1">
                <div className="p-1.5 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                  <ExclamationTriangleIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">License Alert Settings</h2>
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400 ml-9">{t('form.sectionDescLicenseAlerts')}</p>
            </div>

            <div className="space-y-4">
              {/* Notification Channels */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Notification Channels
                </label>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">In-App Notifications</span>
                    <ToggleSwitch
                      checked={licenseAlertSettings?.app || false}
                      onChange={(checked) => {
                        setLicenseAlertSettings(prev => {
                          const newSettings = {
                            ...prev || { app: false, email: false },
                            app: checked,
                          };
                          // If both channels are now disabled, disable all thresholds
                          if (!newSettings.app && !newSettings.email) {
                            if (newSettings.thresholds) {
                              if (newSettings.thresholds.license_count) {
                                newSettings.thresholds.license_count.enabled = false;
                              }
                              if (newSettings.thresholds.expiry_time) {
                                newSettings.thresholds.expiry_time.enabled = false;
                              }
                            }
                          }
                          return newSettings;
                        });
                        setHasUnsavedChanges(true);
                      }}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">Email Notifications</span>
                    <ToggleSwitch
                      checked={licenseAlertSettings?.email || false}
                      onChange={(checked) => {
                        setLicenseAlertSettings(prev => {
                          const newSettings = {
                            ...prev || { app: false, email: false },
                            email: checked,
                          };
                          // If both channels are now disabled, disable all thresholds
                          if (!newSettings.app && !newSettings.email) {
                            if (newSettings.thresholds) {
                              if (newSettings.thresholds.license_count) {
                                newSettings.thresholds.license_count.enabled = false;
                              }
                              if (newSettings.thresholds.expiry_time) {
                                newSettings.thresholds.expiry_time.enabled = false;
                              }
                            }
                          }
                          return newSettings;
                        });
                        setHasUnsavedChanges(true);
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* License Count Threshold */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    License Count Threshold
                  </label>
                  <ToggleSwitch
                    checked={licenseAlertSettings?.thresholds?.license_count?.enabled || false}
                    onChange={(checked) => {
                      // Prevent enabling if both channels are disabled
                      if (checked && (!licenseAlertSettings?.app && !licenseAlertSettings?.email)) {
                        toast.error('Please enable at least one notification channel before enabling thresholds');
                        return;
                      }
                      setLicenseAlertSettings(prev => ({
                        ...prev || { app: false, email: false },
                        thresholds: {
                          ...prev?.thresholds,
                          license_count: {
                            enabled: checked,
                            value: prev?.thresholds?.license_count?.value || 10,
                            unit: prev?.thresholds?.license_count?.unit || 'count',
                          },
                        },
                      }));
                      setHasUnsavedChanges(true);
                    }}
                  />
                </div>
                {licenseAlertSettings?.thresholds?.license_count?.enabled && (
                  <div className="space-y-2 mt-2">
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={licenseAlertSettings.thresholds.license_count.value || 10}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10) || 0;
                          setLicenseAlertSettings(prev => ({
                            ...prev || { app: false, email: false },
                            thresholds: {
                              ...prev?.thresholds,
                              license_count: {
                                enabled: true,
                                value,
                                unit: prev?.thresholds?.license_count?.unit || 'count',
                              },
                            },
                          }));
                          setHasUnsavedChanges(true);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                        placeholder="10"
                      />
                      <select
                        value={licenseAlertSettings.thresholds.license_count.unit || 'count'}
                        onChange={(e) => {
                          setLicenseAlertSettings(prev => ({
                            ...prev || { app: false, email: false },
                            thresholds: {
                              ...prev?.thresholds,
                              license_count: {
                                enabled: true,
                                value: prev?.thresholds?.license_count?.value || 10,
                                unit: e.target.value as 'count' | 'percentage',
                              },
                            },
                          }));
                          setHasUnsavedChanges(true);
                        }}
                        className="px-2 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                      >
                        <option value="count">Count</option>
                        <option value="percentage">Percentage</option>
                      </select>
                    </div>
                    <p className="text-xs text-gray-500">
                      Alert when available licenses fall below this threshold
                    </p>
                  </div>
                )}
              </div>

              {/* Expiry Time Threshold */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Expiry Time Threshold
                  </label>
                  <ToggleSwitch
                    checked={licenseAlertSettings?.thresholds?.expiry_time?.enabled || false}
                    onChange={(checked) => {
                      setLicenseAlertSettings(prev => ({
                        ...prev || { app: false, email: false },
                        thresholds: {
                          ...prev?.thresholds,
                          expiry_time: {
                            enabled: checked,
                            value: prev?.thresholds?.expiry_time?.value || 14,
                            unit: prev?.thresholds?.expiry_time?.unit || 'days',
                          },
                        },
                      }));
                      setHasUnsavedChanges(true);
                    }}
                  />
                </div>
                {licenseAlertSettings?.thresholds?.expiry_time?.enabled && (
                  <div className="space-y-2 mt-2">
                    <div className="flex gap-2">
                      <input
                        type="number"
                        min="0"
                        step="1"
                        value={licenseAlertSettings.thresholds.expiry_time.value || 14}
                        onChange={(e) => {
                          const value = parseInt(e.target.value, 10) || 0;
                          setLicenseAlertSettings(prev => ({
                            ...prev || { app: false, email: false },
                            thresholds: {
                              ...prev?.thresholds,
                              expiry_time: {
                                enabled: true,
                                value,
                                unit: prev?.thresholds?.expiry_time?.unit || 'days',
                              },
                            },
                          }));
                          setHasUnsavedChanges(true);
                        }}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                        placeholder="14"
                      />
                      <select
                        value={licenseAlertSettings.thresholds.expiry_time.unit || 'days'}
                        onChange={(e) => {
                          setLicenseAlertSettings(prev => ({
                            ...prev || { app: false, email: false },
                            thresholds: {
                              ...prev?.thresholds,
                              expiry_time: {
                                enabled: true,
                                value: prev?.thresholds?.expiry_time?.value || 14,
                                unit: e.target.value as 'minutes' | 'hours' | 'days' | 'weeks' | 'months',
                              },
                            },
                          }));
                          setHasUnsavedChanges(true);
                        }}
                        className="px-2 py-2 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                      >
                        <option value="minutes">Minutes</option>
                        <option value="hours">Hours</option>
                        <option value="days">Days</option>
                        <option value="weeks">Weeks</option>
                        <option value="months">Months</option>
                      </select>
                    </div>
                    <p className="text-xs text-gray-500">
                      Alert when license expires within this time period
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Right Column - Email Domains & Security */}
      <div className="space-y-8">
        <div className="card border-l-4 border-l-purple-500/50 dark:border-l-purple-400/40">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-1">
              <div className="p-1.5 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                <EnvelopeIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Allowed Email Domains</h2>
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 ml-9">{t('form.sectionDescEmailDomains')}</p>
          </div>

          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Add Email Domain
              </label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  value={newDomainInput}
                  onChange={(e) => setNewDomainInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addEmailDomain()}
                  className={`flex-1 ${inputClass}`}
                  placeholder="example.com"
                />
                <button
                  type="button"
                  onClick={addEmailDomain}
                  className="p-2.5 border-2 border-gray-800 dark:border-gray-200 text-gray-900 dark:text-white bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg shrink-0"
                  title="Add domain"
                >
                  <PlusIcon className="w-5 h-5" />
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Users with these email domains will be auto-assigned to this client
              </p>
            </div>

            <div className="space-y-2">
              {formData.settings.allowed_email_domains.length === 0 ? (
                <div className="text-sm text-yellow-700 italic p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <strong>Warning:</strong> No email domains added. Users will not be auto-assigned to this client. 
                  Consider adding at least one domain to enable automatic user enrollment.
                </div>
              ) : (
                <>
                  {formData.settings.allowed_email_domains.map((domain) => {
                    const isPrimaryDomain = domain.toLowerCase() === formData.domain.trim().toLowerCase();
                    const canRemove = user?.role === 'sysadmin' || !isPrimaryDomain;
                    return (
                      <div
                        key={domain}
                        className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-200 dark:border-gray-600"
                      >
                        <span className="text-sm text-gray-700">{domain}</span>
                        {canRemove ? (
                          <button
                            type="button"
                            onClick={() => removeEmailDomain(domain)}
                            className="text-red-600 hover:text-red-800 text-sm font-medium"
                          >
                            Remove
                          </button>
                        ) : (
                          <span className="text-xs text-gray-500">Client domain (only sysadmin can remove)</span>
                        )}
                      </div>
                    );
                  })}
                  {formData.settings.allowed_email_domains.length === 1 && formData.settings.allowed_email_domains[0] === formData.domain.toLowerCase() && (
                    <div className="text-xs text-yellow-600 italic p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                      Only the client domain is listed. Users with this domain will be auto-assigned.
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Suspend / Delete Client - Only in edit mode and sysadmins. Hide Suspend when already suspended (show Re-activate in banner instead). */}
        {isEditMode && user?.role === 'sysadmin' && (
          <div className="space-y-4">
            {!isSuspended && (
            <div className="card border-l-4 border-l-amber-500/50 dark:border-l-amber-400/40 bg-amber-50/20 dark:bg-amber-900/10">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('clientForm:suspend.title')}</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {t('clientForm:suspend.description')}
                  </p>
                </div>
                <button
                  onClick={() => setShowSuspendModal(true)}
                  className="px-4 py-2.5 text-sm font-medium text-amber-700 dark:text-amber-300 bg-white dark:bg-gray-800 border border-amber-300 dark:border-amber-700 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-colors flex items-center gap-2 shrink-0"
                >
                  <NoSymbolIcon className="w-4 h-4" />
                  {t('clientForm:suspend.button')}
                </button>
              </div>
            </div>
            )}
            <div className="card border-l-4 border-l-red-500/50 dark:border-l-red-400/40 bg-red-50/20 dark:bg-red-900/10">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('clientForm:delete.title')}</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    {t('clientForm:delete.description')}
                  </p>
                </div>
                <button
                  onClick={() => setShowDeleteModal(true)}
                  className="px-4 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 bg-white dark:bg-gray-800 border border-red-300 dark:border-red-700 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex items-center gap-2 shrink-0"
                >
                  <TrashIcon className="w-4 h-4" />
                  {t('clientForm:delete.button')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    );
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- reserved for Infisical create-org UI
  const handleCreateInfisicalOrg = async () => {
    if (!id || !infisicalOrgName.trim()) {
      toast.error('Please enter an organization name');
      return;
    }

    setCreatingInfisicalOrg(true);
    try {
      await apiService.post(`/company-resources/${id}/infisical/shared-org`, {
        org_name: infisicalOrgName.trim(),
      });
      toast.success('Infisical organization created successfully!');
      setInfisicalOrgName('');
      queryClient.invalidateQueries({ queryKey: ['company-resources', id] });
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to create organization');
    } finally {
      setCreatingInfisicalOrg(false);
    }
  };

  const buildTenantDbRequest = (): { connection_string?: string; host?: string; port?: string; user?: string; password?: string; database?: string; sslmode?: string } => {
    if (dbUseConnectionString) {
      return { connection_string: dbConnectionString.trim() };
    }
    return { host: dbHost.trim(), port: dbPort.trim() || '5432', user: dbUser.trim(), password: dbPassword, database: dbDatabase.trim(), sslmode: dbSslMode || 'prefer' };
  };

  const handleTestTenantDb = async () => {
    if (!id) return;
    const req = buildTenantDbRequest();
    if (dbUseConnectionString ? !req.connection_string : !req.host || !req.user || !req.database) {
      toast.error('Please fill in connection string or host, user, and database');
      return;
    }
    setDbTestError(null);
    setDbTestSuccess(false);
    setDbTestLoading(true);
    try {
      await apiService.testTenantDatabase(id, req);
      setDbTestSuccess(true);
      toast.success('Connection successful');
    } catch (e: any) {
      const msg = e.response?.data?.error || e.message || 'Connection failed';
      setDbTestError(msg);
      toast.error(msg);
    } finally {
      setDbTestLoading(false);
    }
  };

  const handleSetTenantDb = async () => {
    if (!id) return;
    const req = buildTenantDbRequest();
    if (dbUseConnectionString ? !req.connection_string : !req.host || !req.user || !req.database) {
      toast.error('Please fill in connection string or host, user, and database');
      return;
    }
    setDbSaveLoading(true);
    setDbTestError(null);
    try {
      await apiService.setTenantDatabase(id, req);
      toast.success('Database configuration saved');
      queryClient.invalidateQueries({ queryKey: ['company-resources', id] });
    } catch (e: any) {
      const msg = e.response?.data?.error || e.message || 'Save failed';
      setDbTestError(msg);
      toast.error(msg);
    } finally {
      setDbSaveLoading(false);
    }
  };

  const handleBackupTenantDb = async () => {
    if (!id) return;
    setDbBackupLoading(true);
    try {
      const { blob, filename } = await apiService.getTenantDatabaseBackup(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Backup downloaded');
    } catch (e: any) {
      toast.error(e.response?.data?.error || e.message || 'Backup failed');
    } finally {
      setDbBackupLoading(false);
    }
  };

  const handleResetTenantDb = async () => {
    if (!id) return;
    if (backupBeforeReset) {
      setDbResetLoading(true);
      try {
        const { blob, filename } = await apiService.getTenantDatabaseBackup(id);
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Backup downloaded');
      } catch (e: any) {
        toast.error(e.response?.data?.error || e.message || 'Backup failed');
        setDbResetLoading(false);
        return;
      }
      setDbResetLoading(false);
    }
    setResetModalOpen(false);
    setDbResetLoading(true);
    try {
      await apiService.resetTenantDatabase(id);
      toast.success('Database reset and re-initialized');
      queryClient.invalidateQueries({ queryKey: ['company-resources', id] });
    } catch (e: any) {
      toast.error(e.response?.data?.error || e.message || 'Reset failed');
    } finally {
      setDbResetLoading(false);
    }
  };

  // Resources tab is hidden; function kept for when tab is re-enabled
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const renderInfrastructure = () => {
    return (
      <div className="space-y-6">
        {/* Sysadmin: enable company admins to see Resources and choose which database options they get */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Client admin access to Resources</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            When enabled, client admins will see &quot;Resources&quot; in the sidebar. Use the switches below to choose which database options they can use.
          </p>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-900 dark:text-white">Show Resources in sidebar for client admins</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={companyResources?.allow_company_admins_resources ?? false}
                  onChange={(e) => handleSetAllowCompanyAdminsFlags({ allow_company_admins_resources: e.target.checked })}
                  disabled={allowResourcesSaving}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-governate-500 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-governate-600" />
                <span className="ml-3 text-sm">{companyResources?.allow_company_admins_resources ? 'Enabled' : 'Disabled'}</span>
              </label>
            </div>
            {companyResources?.allow_company_admins_resources && (
              <>
                <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-600 pt-4">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Allow spin up new database (Governate Cloud SQL)</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={companyResources?.allow_company_admins_spin_up_db ?? false}
                      onChange={(e) => handleSetAllowCompanyAdminsFlags({ allow_company_admins_spin_up_db: e.target.checked })}
                      disabled={allowResourcesSaving}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-governate-500 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-governate-600" />
                    <span className="ml-3 text-sm">{companyResources?.allow_company_admins_spin_up_db ? 'Enabled' : 'Disabled'}</span>
                  </label>
                </div>
                <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-600 pt-4">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Allow bring your own PostgreSQL</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={companyResources?.allow_company_admins_bring_own_db ?? false}
                      onChange={(e) => handleSetAllowCompanyAdminsFlags({ allow_company_admins_bring_own_db: e.target.checked })}
                      disabled={allowResourcesSaving}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-governate-500 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-governate-600" />
                    <span className="ml-3 text-sm">{companyResources?.allow_company_admins_bring_own_db ? 'Enabled' : 'Disabled'}</span>
                  </label>
                </div>
                <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-600 pt-4">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">Allow client admins to backup and reset database</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={companyResources?.allow_company_admins_backup_db ?? false}
                      onChange={(e) => handleSetAllowCompanyAdminsFlags({ allow_company_admins_backup_db: e.target.checked })}
                      disabled={allowResourcesSaving}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-governate-500 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-governate-600" />
                    <span className="ml-3 text-sm">{companyResources?.allow_company_admins_backup_db ? 'Enabled' : 'Disabled'}</span>
                  </label>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Database: dedicated (we provision) or bring your own */}
        <div className="card">
          <div className="flex items-center space-x-2 mb-4">
            <CircleStackIcon className="w-5 h-5 text-governate-600" />
            <h2 className="text-lg font-semibold text-gray-900">Database</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            This client can use the shared database (default) or its own PostgreSQL. Only clients created by a sysadmin can use a dedicated or customer-owned database.
          </p>
          <div className="grid gap-4 sm:grid-cols-2 mb-4">
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50/50">
              <div className="flex items-center space-x-2 mb-2">
                <CloudIcon className="w-5 h-5 text-blue-600" />
                <span className="font-medium text-gray-900">Spin up new database</span>
              </div>
              <p className="text-sm text-gray-600">
                Provision a new database on Governate Cloud SQL (same instance, new database). We run init/migrations and store the connection securely. Coming soon.
              </p>
            </div>
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50/50">
              <div className="flex items-center space-x-2 mb-2">
                <ServerIcon className="w-5 h-5 text-green-600" />
                <span className="font-medium text-gray-900">Bring your own</span>
              </div>
              <p className="text-sm text-gray-600">
                The client provides their own Postgres. Enter connection details below, test, then save. Client admins can also add their own DB from the Resources page.
              </p>
              <p className="text-xs text-amber-700 dark:text-amber-400 mt-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded px-2 py-1.5">
                Host must be reachable from the Governate server. If the app runs in Docker, use the DB service name (e.g. <code className="font-mono">postgres</code>) not <code className="font-mono">localhost</code>.
              </p>
            </div>
          </div>
          {companyResources?.database_mode === 'self' ? (
            <div className="space-y-3">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                This client is configured to use its own database (connection details stored encrypted).
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={handleBackupTenantDb}
                  disabled={dbBackupLoading}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  {dbBackupLoading ? 'Preparing...' : 'Backup database'}
                </button>
                <button
                  type="button"
                  onClick={() => setResetModalOpen(true)}
                  disabled={dbResetLoading}
                  className="px-4 py-2 text-sm font-medium text-amber-800 bg-amber-100 rounded-lg hover:bg-amber-200 disabled:opacity-50"
                >
                  {dbResetLoading ? 'Resetting...' : 'Reset database'}
                </button>
              </div>
              <Modal isOpen={resetModalOpen} onClose={() => setResetModalOpen(false)}>
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Reset database</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    This will run reset.sql then init.sql on this client&apos;s database. All tables and data will be dropped and the schema recreated. This cannot be undone.
                  </p>
                  <label className="flex items-center gap-2 mb-4 cursor-pointer">
                    <input type="checkbox" checked={backupBeforeReset} onChange={(e) => setBackupBeforeReset(e.target.checked)} className="rounded text-governate-600" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Download backup before reset</span>
                  </label>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setResetModalOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                    <button type="button" onClick={handleResetTenantDb} disabled={dbResetLoading} className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 disabled:opacity-50">OK, reset</button>
                  </div>
                </div>
              </Modal>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/30 space-y-4">
              <div className="flex gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={dbUseConnectionString} onChange={() => { setDbUseConnectionString(true); setDbTestError(null); }} className="text-governate-600" />
                  <span className="text-sm font-medium">Connection string</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="radio" checked={!dbUseConnectionString} onChange={() => { setDbUseConnectionString(false); setDbTestError(null); }} className="text-governate-600" />
                  <span className="text-sm font-medium">Individual fields</span>
                </label>
              </div>
              {dbUseConnectionString ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PostgreSQL connection string</label>
                  <textarea
                    value={dbConnectionString}
                    onChange={(e) => { setDbConnectionString(e.target.value); setDbTestError(null); }}
                    placeholder="postgres://user:password@host:5432/database?sslmode=require"
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500 focus:border-transparent font-mono text-sm"
                  />
                </div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Host</label>
                    <input type="text" value={dbHost} onChange={(e) => setDbHost(e.target.value)} placeholder="postgres or localhost" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500" />
                    <span className="text-xs text-gray-500">Use name server can reach (e.g. postgres in Docker)</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Port</label>
                    <input type="text" value={dbPort} onChange={(e) => setDbPort(e.target.value)} placeholder="5432" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">User</label>
                    <input type="text" value={dbUser} onChange={(e) => setDbUser(e.target.value)} placeholder="postgres" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
                    <input type="password" value={dbPassword} onChange={(e) => setDbPassword(e.target.value)} placeholder="••••••••" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Database</label>
                    <input type="text" value={dbDatabase} onChange={(e) => setDbDatabase(e.target.value)} placeholder="mydb" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">SSL mode</label>
                    <select value={dbSslMode} onChange={(e) => setDbSslMode(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-governate-500">
                      <option value="disable">disable</option>
                      <option value="prefer">prefer</option>
                      <option value="require">require</option>
                      <option value="verify-full">verify-full</option>
                    </select>
                  </div>
                </div>
              )}
              {dbTestError && <p className="text-sm text-red-600">{dbTestError}</p>}
              {dbTestSuccess && <p className="text-sm text-green-600">Connection successful. You can save below.</p>}
              <div className="flex gap-3">
                <button type="button" onClick={handleTestTenantDb} disabled={dbTestLoading} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                  {dbTestLoading ? 'Testing...' : 'Test connection'}
                </button>
                <button type="button" onClick={handleSetTenantDb} disabled={dbSaveLoading} className="px-4 py-2 text-sm font-medium text-white bg-governate-600 rounded-lg hover:bg-governate-700 disabled:opacity-50">
                  {dbSaveLoading ? 'Saving...' : 'Save & use this database'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Main render logic
  return (
    <div className="space-y-6">
      {/* Wizard Mode (New Client) */}
      {!isEditMode && (
        <>
          {/* Step Indicator */}
          <div className="rounded-xl bg-gray-100 dark:bg-gray-800/50 p-2 border border-gray-200 dark:border-gray-700 max-w-xl mx-auto">
            <div className="flex items-center gap-2">
              <StepIndicator
                step={1}
                title={t('clientForm:tabs.basicInfo')}
                isActive={currentStep === 1}
                isCompleted={currentStep > 1}
              />
              <div className="flex-1 h-0.5 bg-gray-300 dark:bg-gray-600 rounded mx-2" />
              <StepIndicator
                step={2}
                title={t('clientForm:tabs.subscription')}
                isActive={currentStep === 2}
                isCompleted={false}
              />
            </div>
          </div>

          {/* Step Content */}
          <div className="mt-8">
            {currentStep === 1 && renderBasicInfo()}
            {currentStep === 2 && renderSubscriptionSettings()}
          </div>

          {/* Wizard Navigation */}
          <div className="mt-8 flex justify-between items-center gap-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 p-4">
            <button
              type="button"
              onClick={handlePreviousStep}
              disabled={currentStep === 1}
              className={`px-5 py-2.5 rounded-lg border text-sm font-medium transition-colors ${
                currentStep === 1
                  ? 'border-gray-200 dark:border-gray-600 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700'
              }`}
            >
              Previous
            </button>

            <span className="text-sm text-gray-500 dark:text-gray-400">
              Step {currentStep} of 2
            </span>

            {currentStep < 2 ? (
              <button
                type="button"
                onClick={handleNextStep}
                className="px-5 py-2.5 text-sm font-medium bg-governate-500 text-white rounded-lg hover:bg-governate-600 transition-colors"
              >
                {t('clientForm:buttons.next')}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleFinish}
                className="px-5 py-2.5 text-sm font-medium bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                {t('clientForm:buttons.createClient')}
              </button>
            )}
          </div>
        </>
      )}

      {/* Edit Mode (Existing Client) - Tabbed Interface */}
      {isEditMode && (
        <>
          {/* Tab Navigation */}
          <div className="rounded-xl bg-gray-100 dark:bg-gray-800/50 p-1.5 inline-flex gap-1 border border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={() => setActiveTab('basic')}
              className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg transition-all ${
                activeTab === 'basic'
                  ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm border border-gray-200 dark:border-gray-600'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-gray-700/50'
              }`}
            >
              <BuildingOfficeIcon className="w-5 h-5 shrink-0" />
              <span>{t('clientForm:tabs.basicInfo')}</span>
            </button>
            {user?.role === 'sysadmin' && (
              <>
                <button
                  type="button"
                  onClick={() => setActiveTab('subscription')}
                  className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg transition-all ${
                    activeTab === 'subscription'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm border border-gray-200 dark:border-gray-600'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <UsersIcon className="w-5 h-5 shrink-0" />
                  <span>{t('clientForm:tabs.subscription')}</span>
                </button>
                {/* Resources tab hidden for now – uncomment to re-enable
                <button
                  type="button"
                  onClick={() => setActiveTab('infrastructure')}
                  className={`flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-lg transition-all ${
                    activeTab === 'infrastructure'
                      ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm border border-gray-200 dark:border-gray-600'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-gray-700/50'
                  }`}
                >
                  <CircleStackIcon className="w-5 h-5 shrink-0" />
                  <span>Resources</span>
                </button>
                */}
              </>
            )}
          </div>

          {/* Tab Content */}
          <div className="mt-6">
            {activeTab === 'basic' && renderBasicInfo()}
            {activeTab === 'subscription' && user?.role === 'sysadmin' && renderSubscriptionSettings()}
            {/* Resources tab hidden for now – uncomment to re-enable
            {activeTab === 'infrastructure' && user?.role === 'sysadmin' && renderInfrastructure()}
            */}
          </div>
        </>
      )}

      {/* Delete Client Confirmation Modal */}
      {showDeleteModal && isEditMode && id && createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
              onClick={() => !isDeletingCompany && setShowDeleteModal(false)}
            ></div>
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 sm:mx-0 sm:h-10 sm:w-10">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                      {t('clientForm:modals.deleteTitle')}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('clientForm:modals.deleteConfirm', { name: companyData?.name || t('clientForm:thisClient') })}
                      </p>
                      {isDeletingCompany && (
                        <p className="mt-2 text-sm text-governate-600 dark:text-governate-400">
                          {deleteCompanyProgressStep === 0 && t('clientForm:modals.progressRemoving')}
                          {deleteCompanyProgressStep === 1 && t('clientForm:modals.progressPolicies')}
                          {deleteCompanyProgressStep === 2 && t('clientForm:modals.progressCleaning')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  onClick={async () => {
                    if (!id) return;
                    setIsDeletingCompany(true);
                    try {
                      await apiService.delete(`/companies/${id}`, { timeout: 120000 });
                      toast.success(t('clientForm:toasts.deleted'));
                      queryClient.invalidateQueries({ queryKey: ['companies'] });
                      navigate(CLIENT_LIST_PATH);
                    } catch (err: any) {
                      toast.error(err.response?.data?.error || t('clientForm:toasts.deleteFailed'));
                    } finally {
                      setIsDeletingCompany(false);
                      setShowDeleteModal(false);
                    }
                  }}
                  disabled={isDeletingCompany}
                  className="w-full inline-flex justify-center items-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {isDeletingCompany ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                      {t('clientForm:modals.deleting')}
                    </>
                  ) : (
                    t('clientForm:buttons.delete')
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeletingCompany}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-governate-500 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto sm:text-sm"
                >
                  {t('clientForm:modals.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Suspend Client Confirmation Modal */}
      {showSuspendModal && isEditMode && id && createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
              onClick={() => !isSuspendingCompany && setShowSuspendModal(false)}
            />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-amber-100 dark:bg-amber-900/30 sm:mx-0 sm:h-10 sm:w-10">
                    <NoSymbolIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                      {t('clientForm:modals.suspendTitle')}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('clientForm:modals.suspendConfirm', { name: companyData?.name || t('clientForm:thisClient') })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  onClick={async () => {
                    if (!id) return;
                    setIsSuspendingCompany(true);
                    try {
                      await apiService.post(`/companies/${id}/suspend`);
                      toast.success(t('clientForm:toasts.suspended'));
                      await queryClient.refetchQueries({ queryKey: ['subscription', 'company', id] });
                      queryClient.invalidateQueries({ queryKey: ['company', id] });
                      setShowSuspendModal(false);
                    } catch (err: any) {
                      toast.error(err.response?.data?.error || t('clientForm:toasts.suspendFailed'));
                    } finally {
                      setIsSuspendingCompany(false);
                    }
                  }}
                  disabled={isSuspendingCompany}
                  className="w-full inline-flex justify-center items-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-amber-600 text-base font-medium text-white hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {isSuspendingCompany ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      {t('clientForm:modals.suspending')}
                    </>
                  ) : (
                    t('clientForm:buttons.suspend')
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowSuspendModal(false)}
                  disabled={isSuspendingCompany}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-governate-500 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto sm:text-sm"
                >
                  {t('clientForm:modals.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Re-activate Client Modal */}
      {showReactivateModal && isEditMode && id && createPortal(
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
              onClick={() => !isReactivatingCompany && setShowReactivateModal(false)}
            />
            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 sm:mx-0 sm:h-10 sm:w-10">
                    <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-gray-100">
                      {t('clientForm:modals.reactivateTitle')}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {t('clientForm:modals.reactivateConfirm', { name: companyData?.name || t('clientForm:thisClient') })}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse gap-3">
                <button
                  type="button"
                  onClick={async () => {
                    if (!id) return;
                    setIsReactivatingCompany(true);
                    try {
                      const licensedUntilISO = licensedUntil
                        ? (() => {
                            const d = new Date(licensedUntil.replace(' ', 'T'));
                            return isNaN(d.getTime()) ? null : d.toISOString();
                          })()
                        : null;
                      await apiService.post(`/companies/${id}/reactivate`, {
                        max_full_licenses: formData.total_licenses,
                        licensed_until: licensedUntilISO,
                        reader_licenses_per_full: formData.settings.reader_licenses_per_full ?? undefined,
                        max_auditor_licenses: formData.settings.max_auditor_licenses ?? undefined,
                        max_allowed_devices_per_user: formData.settings.max_allowed_devices_per_user ?? undefined,
                        max_folders_per_device: formData.settings.max_folders_per_device ?? undefined,
                      });
                      toast.success(t('clientForm:toasts.reactivated'));
                      queryClient.invalidateQueries({ queryKey: ['subscription', 'company', id] });
                      queryClient.invalidateQueries({ queryKey: ['company', id] });
                      setShowReactivateModal(false);
                    } catch (err: any) {
                      toast.error(err.response?.data?.error || t('clientForm:toasts.reactivateFailed'));
                    } finally {
                      setIsReactivatingCompany(false);
                    }
                  }}
                  disabled={isReactivatingCompany}
                  className="w-full inline-flex justify-center items-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-governate-600 text-base font-medium text-white hover:bg-governate-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-governate-500 disabled:opacity-50 disabled:cursor-not-allowed sm:ml-3 sm:w-auto sm:text-sm"
                >
                  {isReactivatingCompany ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Re-activating...
                    </>
                  ) : (
                    <>{t('clientForm:buttons.reactivate')}</>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowReactivateModal(false)}
                  disabled={isReactivatingCompany}
                  className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-governate-500 disabled:opacity-50 disabled:cursor-not-allowed sm:mt-0 sm:w-auto sm:text-sm"
                >
                  {t('clientForm:modals.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Send invite email modal (preview + send) */}
      {showSendInviteModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t('clientForm:modals.sendInviteTitle')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {t('clientForm:modals.sendInvitePreview', { email: formData.contact_person_email })}
              </p>
            </div>
            <div className="px-6 py-4 flex-1 overflow-y-auto">
              {sendInvitePreview && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject</label>
                    <div className="p-2 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-600 text-sm">
                      {sendInvitePreview.subject
                        .replace(/\{\{company_name\}\}/gi, companyData?.name || formData.name || 'Client')
                        .replace(/\{\{accept_link\}\}/gi, '#')
                        .replace(/\{\{invitee_email\}\}/gi, formData.contact_person_email)}
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subscription note (in email body)</label>
                    <textarea
                      value={sendInviteSubscriptionNote}
                      onChange={(e) => setSendInviteSubscriptionNote(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-governate-500 focus:border-transparent text-sm"
                      placeholder={defaultSubscriptionNote}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Preview</label>
                    <div
                      className="p-4 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-600 text-sm prose prose-sm max-w-none dark:prose-invert"
                      dangerouslySetInnerHTML={{
                        __html: sendInvitePreview.html_content
                          .replace(/\{\{company_name\}\}/gi, companyData?.name || formData.name || 'Client')
                          .replace(/\{\{accept_link\}\}/gi, '#')
                          .replace(/\{\{invitee_email\}\}/gi, formData.contact_person_email)
                          .replace(/\{\{subscription_note\}\}/gi, sendInviteSubscriptionNote || '—'),
                      }}
                    />
                  </div>
                </>
              )}
              {!sendInvitePreview && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-governate-500" />
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowSendInviteModal(false);
                  setSendInvitePreview(null);
                }}
                disabled={sendInviteSending}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                {t('clientForm:modals.cancel')}
              </button>
              <button
                type="button"
                disabled={sendInviteSending || !sendInvitePreview || !id}
                onClick={async () => {
                  if (!id) return;
                  setSendInviteSending(true);
                  try {
                    await apiService.sendCompanyAdminInvite(id, {
                      subscription_note: sendInviteSubscriptionNote?.trim() || undefined,
                    });
                    toast.success(t('clientForm:toasts.inviteSent'));
                    setShowSendInviteModal(false);
                    setSendInvitePreview(null);
                    queryClient.invalidateQueries({ queryKey: ['company', id] });
                    queryClient.invalidateQueries({ queryKey: ['invitations', 'company', id] });
                  } catch (e: any) {
                    toast.error(e.response?.data?.error || t('clientForm:toasts.inviteSendFailed'));
                  } finally {
                    setSendInviteSending(false);
                  }
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-governate-600 rounded-lg hover:bg-governate-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendInviteSending ? t('clientForm:modals.sending') : t('clientForm:buttons.sendInvite')}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Managed By Selection Modal */}
      {showManagedByModal && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Select Managing Client</h3>
            </div>
            <div className="px-6 py-4 flex-1 overflow-y-auto">
              {/* Search */}
              <div className="mb-4">
                <div className="relative">
                  <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by name or domain..."
                    value={managedBySearchTerm}
                    onChange={(e) => setManagedBySearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-governate-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Client list (managed by) */}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {/* Option to remove managed_by */}
                <button
                  onClick={() => {
                    updateField('managed_by', null);
                    setShowManagedByModal(false);
                    setManagedBySearchTerm('');
                  }}
                  className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                    !formData.managed_by
                      ? 'border-governate-500 bg-governate-50 dark:bg-governate-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-gray-100">No Client (Self-Managed)</div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Remove managed_by assignment</div>
                    </div>
                  </div>
                </button>

                {filteredManagedByCompanies.map((company: any) => (
                  <button
                    key={company.id}
                    onClick={() => {
                      updateField('managed_by', company.id);
                      setShowManagedByModal(false);
                      setManagedBySearchTerm('');
                    }}
                    className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-colors ${
                      formData.managed_by === company.id
                        ? 'border-governate-500 bg-governate-50 dark:bg-governate-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <BuildingOfficeIcon className="w-5 h-5 text-gray-400" />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 dark:text-gray-100 truncate">{company.name}</div>
                        {company.domain && (
                          <div className="text-sm text-gray-500 dark:text-gray-400 truncate">{company.domain}</div>
                        )}
                      </div>
                      {formData.managed_by === company.id && (
                        <CheckCircleIcon className="w-5 h-5 text-governate-500 flex-shrink-0" />
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                onClick={() => {
                  setShowManagedByModal(false);
                  setManagedBySearchTerm('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CompanyForm;

